import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { useAuth } from '@/hooks/useAuth';
import { useDebounce } from '@/hooks/useDebounce';
import { useIsMobile } from '@/hooks/use-mobile';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DataTable } from '@/components/common/DataTable';
import { StatusBadge } from '@/components/common/StatusBadge';
import { ShipmentFilterSidebar, FilterValues } from '@/components/features/shipments/ShipmentFilterSidebar';
import { BulkPrintLabelDialog } from '@/components/features/shipments/BulkPrintLabelDialog';
import { LABEL_OPTIONS, LabelType } from '@/components/features/shipments/PrintLabelDialog';
import { Shipment } from '@/types/shipment.types';
import { formatDate } from '@/lib/formatters';
import { PackageOpen, Filter, ChevronRight, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const initialFilters: FilterValues = {
  search: '',
  status_code: '',
  city: '',
  service_type: '',
  driver_id: '',
  carrier_id: '',
  created_from: '',
  created_to: '',
};

interface PrintShipment extends Shipment {
  tenant?: { id: string; name: string; phone: string; address: string | null };
}

export default function BarcodePrint() {
  const navigate = useNavigate();
  const { get, loading } = useApi();
  const { user } = useAuth();
  const isMobile = useIsMobile();

  const [shipments, setShipments] = useState<PrintShipment[]>([]);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [filters, setFilters] = useState<FilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const [labelChooserOpen, setLabelChooserOpen] = useState(false);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [labelType, setLabelType] = useState<LabelType>('thermal');

  const debouncedSearch = useDebounce(filters.search, 300);

  useEffect(() => {
    fetchShipments();
  }, [
    debouncedSearch,
    filters.status_code,
    filters.city,
    filters.service_type,
    filters.carrier_id,
    filters.created_from,
    filters.created_to,
  ]);

  const fetchShipments = async () => {
    try {
      const params: Record<string, any> = { limit: 2000 };
      if (debouncedSearch) params.search = debouncedSearch;
      if (filters.status_code) params.status_code = filters.status_code;
      if (filters.city) params.city = filters.city;
      if (filters.service_type) params.service_type_id = filters.service_type;
      if (filters.carrier_id) params.carrier_id = filters.carrier_id;
      if (filters.created_from) params.created_from = filters.created_from;
      if (filters.created_to) params.created_to = filters.created_to;

      const response: any = await get('/api/v1/dashboard/shipments/print-list', params);
      setShipments(response.list ?? []);
    } catch (error) {
      console.error('Failed to fetch shipments:', error);
    }
  };

  const handleClearFilters = () => {
    setFilters(initialFilters);
  };

  const handleFilterChange = (key: keyof FilterValues, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value === 'all' ? '' : value }));
  };

  const selectedShipments = shipments
    .filter((s) => selectedRows.includes(s.id))
    .map((s) => ({
      ...s,
      // Label templates expect service_type as a string, not an object
      service_type: (s as any).service_type?.name ?? '',
    }));

  const handlePrintClick = () => {
    if (selectedShipments.length === 0) return;
    setLabelChooserOpen(true);
  };

  const handleLabelTypeChosen = (type: LabelType) => {
    setLabelType(type);
    setLabelChooserOpen(false);
    setBulkDialogOpen(true);
  };

  const columns = [
    {
      header: 'رقم التتبع',
      accessor: 'tracking_number' as keyof PrintShipment,
      className: 'font-medium',
    },
    {
      header: 'الحالة',
      accessor: (row: PrintShipment) =>
        row.status ? <StatusBadge status={row.status.name_ ?? row.status.code ?? ''} /> : '-',
    },
    {
      header: 'العنوان',
      accessor: (row: PrintShipment) => {
        const city = row.to_address?.city ?? '';
        const neighborhood = row.to_address?.neighborhood ?? '';
        return [city, neighborhood].filter(Boolean).join(' - ') || '-';
      },
    },
    {
      header: 'المستلم',
      accessor: (row: PrintShipment) => row.recipient_info?.name ?? '-',
    },
    {
      header: 'نوع الخدمة',
      accessor: (row: PrintShipment) =>
        (row as any).service_type?.name ?? '-',
    },
    {
      header: user?.role === 'tenant' ? 'الناقل' : 'العميل',
      accessor: (row: PrintShipment) =>
        user?.role === 'tenant'
          ? (row as any).carrier?.name ?? '-'
          : row.tenant?.name ?? '-',
    },
    {
      header: 'القيمة',
      accessor: (row: PrintShipment) => `${row.shipment_value ?? 0}`,
    },
    {
      header: 'التاريخ',
      accessor: (row: PrintShipment) => formatDate(row.created_at),
      className: 'text-muted-foreground',
    },
  ];

  const FilterSidebarContent = (
    <ShipmentFilterSidebar
      filters={filters}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
    />
  );

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">طباعة الباركود</h1>
              <p className="text-sm text-muted-foreground">
                اختر الشحنات ثم اطبع الملصقات دفعة واحدة
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Filter className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-4">
                <div className="mt-6">{FilterSidebarContent}</div>
              </SheetContent>
            </Sheet>
            <Button
              onClick={handlePrintClick}
              disabled={selectedShipments.length === 0}
              className="gap-2"
            >
              <Printer className="h-4 w-4" />
              طباعة ({selectedShipments.length})
            </Button>
          </div>
        </div>
      </div>

      <div className="flex gap-4 ">
        <div className="hidden lg:block">{FilterSidebarContent}</div>

        <div className="flex-1 min-w-0">
          {loading ? (
            <LoadingSkeleton type={isMobile ? 'card' : 'table'} rows={isMobile ? 5 : 10} />
          ) : shipments.length === 0 ? (
            <EmptyState
              icon={<PackageOpen className="h-16 w-16" />}
              title="لا توجد شحنات"
              description="لم يتم العثور على أي شحنات تطابق معايير البحث"
              action={
                Object.values(filters).some((v) => v !== '')
                  ? { label: 'مسح الفلاتر', onClick: handleClearFilters }
                  : undefined
              }
            />
          ) : (
            <DataTable
              data={shipments}
              columns={columns}
              selectable={true}
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
              emptyMessage="لا توجد شحنات"
            />
          )}
        </div>
      </div>

      {/* Label-type chooser */}
      <Dialog open={labelChooserOpen} onOpenChange={setLabelChooserOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>اختر نوع الملصق</DialogTitle>
            <DialogDescription>
              سيتم طباعة {selectedShipments.length} ملصق بالنوع المختار
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {LABEL_OPTIONS.map((opt) => (
              <button
                key={opt.type}
                onClick={() => handleLabelTypeChosen(opt.type)}
                className="w-full text-end border rounded-lg p-3 hover:bg-muted transition-colors"
              >
                <div className="font-medium">{opt.name}</div>
                <div className="text-xs text-muted-foreground">{opt.description}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk print preview */}
      {bulkDialogOpen && (
        <BulkPrintLabelDialog
          open={bulkDialogOpen}
          onOpenChange={setBulkDialogOpen}
          shipments={selectedShipments as any}
          labelType={labelType}
        />
      )}
    </PageContainer>
  );
}
